// Single source of truth for unit handling in Pathfinder.
//
// Values are always stored internally in each family's `base` unit.
// The registry tells every input, output, chart and report:
//   1. what units exist and how they convert (`units[unit].toBase`)
//   2. which unit to auto-pick for display at a given magnitude (`displayBands`)
//   3. which units to offer in input dropdowns (`inputUnits`)

export const QUANTITIES = {
  length: {
    base: "m",
    defaultPrecision: 2,
    systemAgnostic: false,
    fixed: false,
    // Invariant: length unit ids match symbols (e.g. "km" -> symbol "km").
    // getFamilyForUnit / getConvertibleAxisSourceUnit rely on ids in chart paths.
    units: {
      mm: {
        toBase: 0.001,
        symbol: "mm",
        system: "metric",
        labelKey: "units.length.millimeter"
      },
      m: {
        toBase: 1,
        symbol: "m",
        system: "metric",
        labelKey: "units.length.meter"
      },
      km: {
        toBase: 1000,
        symbol: "km",
        system: "metric",
        labelKey: "units.length.kilometer"
      },
      in: {
        toBase: 0.0254,
        symbol: "in",
        system: "imperial",
        labelKey: "units.length.inch"
      },
      ft: {
        toBase: 0.3048,
        symbol: "ft",
        system: "imperial",
        labelKey: "units.length.foot"
      },
      yd: {
        toBase: 0.9144,
        symbol: "yd",
        system: "imperial",
        labelKey: "units.length.yard"
      },
      mi: {
        toBase: 1609.344,
        symbol: "mi",
        system: "imperial",
        labelKey: "units.length.mile"
      }
    },
    displayBands: {
      metric: [{ maxBase: 1000, unit: "m" }, { unit: "km" }],
      imperial: [
        { maxBase: 0.3048, unit: "in" },
        { maxBase: 1609.344, unit: "ft" },
        { unit: "mi" }
      ]
    },
    inputUnits: {
      // Miles excluded from inputs; still available for auto-picked outputs.
      metric: ["mm", "m", "km"],
      imperial: ["in", "ft", "yd"]
    },
    metricToImperialMap: {
      mm: "in",
      m: "ft",
      km: "mi"
    }
  },

  weight: {
    base: "kg",
    defaultPrecision: 2,
    systemAgnostic: false,
    fixed: false,
    units: {
      kg: {
        toBase: 1,
        symbol: "kg",
        system: "metric",
        labelKey: "units.weight.kilogram"
      },
      g: {
        toBase: 0.001,
        symbol: "g",
        system: "metric",
        labelKey: "units.weight.gram"
      },
      t: {
        toBase: 1000,
        symbol: "t",
        system: "metric",
        labelKey: "units.weight.tonne"
      },
      lb: {
        toBase: 0.45359237,
        symbol: "lb",
        system: "imperial",
        labelKey: "units.weight.pound"
      },
      oz: {
        toBase: 0.02834952,
        symbol: "oz",
        system: "imperial",
        labelKey: "units.weight.ounce"
      }
    },
    displayBands: {
      metric: [
        { maxBase: 1, unit: "g" },
        { maxBase: 1000, unit: "kg" },
        { unit: "t" }
      ],
      imperial: [{ maxBase: 0.45359237, unit: "oz" }, { unit: "lb" }]
    },
    inputUnits: {
      metric: ["kg", "g", "t"],
      imperial: ["lb", "oz"]
    },
    metricToImperialMap: {
      kg: "lb",
      g: "oz",
      t: "lb"
    }
  },

  currency_per_length: {
    base: "cur/m",
    systemAgnostic: false,
    units: {
      "cur/m": {
        toBase: 1,
        symbol: "cur/m",
        system: "metric"
      },
      "cur/km": {
        toBase: 0.001,
        symbol: "cur/km",
        system: "metric"
      },
      "cur/ft": {
        toBase: 3.28084,
        symbol: "cur/ft",
        system: "imperial"
      },
      "cur/mi": {
        toBase: 1 / 1609.344,
        symbol: "cur/mi",
        system: "imperial"
      }
    },
    displayBands: {
      metric: [{ unit: "cur/km" }],
      imperial: [{ unit: "cur/mi" }]
    },
    inputUnits: {
      metric: ["cur/m", "cur/km"],
      imperial: ["cur/ft", "cur/mi"]
    },
    metricToImperialMap: {
      "cur/m": "cur/ft",
      "cur/km": "cur/mi"
    }
  },

  score_per_length: {
    base: "score/m",
    systemAgnostic: false,
    units: {
      "score/m": {
        toBase: 1,
        symbol: "score/m",
        system: "metric"
      },
      "score/km": {
        toBase: 0.001,
        symbol: "score/km",
        system: "metric"
      },
      "score/ft": {
        toBase: 3.28084,
        symbol: "score/ft",
        system: "imperial"
      },
      "score/mi": {
        toBase: 1 / 1609.344,
        symbol: "score/mi",
        system: "imperial"
      }
    },
    displayBands: {
      metric: [{ unit: "score/km" }],
      imperial: [{ unit: "score/mi" }]
    },
    inputUnits: {
      metric: ["score/m", "score/km"],
      imperial: ["score/ft", "score/mi"]
    },
    metricToImperialMap: {
      "score/m": "score/ft",
      "score/km": "score/mi"
    }
  },

  weight_per_volume: {
    base: "kg/m3",
    systemAgnostic: false,
    units: {
      "kg/m3": {
        toBase: 1,
        symbol: "kg/m³",
        system: "metric"
      },
      "lb/ft3": {
        toBase: 16.0185,
        symbol: "lb/ft³",
        system: "imperial"
      }
    },
    displayBands: {
      metric: [{ unit: "kg/m3" }],
      imperial: [{ unit: "lb/ft3" }]
    },
    inputUnits: {
      metric: ["kg/m3"],
      imperial: ["lb/ft3"]
    },
    metricToImperialMap: {
      "kg/m3": "lb/ft3"
    }
  },

  resistance_per_area: {
    base: "R/m2",
    systemAgnostic: false,
    units: {
      "R/m2": {
        toBase: 1,
        symbol: "R/m²",
        system: "metric"
      },
      "R/ft2": {
        toBase: 10.7639,
        symbol: "R/ft²",
        system: "imperial"
      }
    },
    displayBands: {
      metric: [{ unit: "R/m2" }],
      imperial: [{ unit: "R/ft2" }]
    },
    inputUnits: {
      metric: ["R/m2"],
      imperial: ["R/ft2"]
    },
    metricToImperialMap: {
      "R/m2": "R/ft2"
    }
  },

  volume_per_time: {
    base: "m3/s",
    systemAgnostic: false,
    units: {
      "m3/s": {
        toBase: 1,
        symbol: "m³/s",
        system: "metric"
      },
      "m3/h": {
        toBase: 1 / 3600,
        symbol: "m³/h",
        system: "metric"
      },
      "ft3/s": {
        toBase: 0.028316846592,
        symbol: "ft³/s",
        system: "imperial"
      },
      "ft3/h": {
        toBase: 0.028316846592 / 3600,
        symbol: "ft³/h",
        system: "imperial"
      }
    },
    displayBands: {
      metric: [{ unit: "m3/h" }],
      imperial: [{ unit: "ft3/h" }]
    },
    inputUnits: {
      metric: ["m3/s", "m3/h"],
      imperial: ["ft3/s", "ft3/h"]
    },
    metricToImperialMap: {
      "m3/s": "ft3/s",
      "m3/h": "ft3/h"
    }
  },

  area: {
    base: "sqm",
    defaultPrecision: 2,
    systemAgnostic: false,
    fixed: false,
    units: {
      sqmm: {
        toBase: 0.000001,
        symbol: "mm²",
        system: "metric",
        labelKey: "units.area.squareMillimeter"
      },
      sqm: {
        toBase: 1,
        symbol: "m²",
        system: "metric",
        labelKey: "units.area.squareMeter"
      },
      ha: {
        toBase: 10000,
        symbol: "ha",
        system: "metric",
        labelKey: "units.area.hectare"
      },
      sqkm: {
        toBase: 1000000,
        symbol: "km²",
        system: "metric",
        labelKey: "units.area.squareKilometer"
      },
      sqin: {
        toBase: 0.00064516,
        symbol: "in²",
        system: "imperial",
        labelKey: "units.area.squareInch"
      },
      sqft: {
        toBase: 0.09290304,
        symbol: "ft²",
        system: "imperial",
        labelKey: "units.area.squareFoot"
      },
      ac: {
        toBase: 4046.8564224,
        symbol: "ac",
        system: "imperial",
        labelKey: "units.area.acre"
      },
      sqmi: {
        toBase: 2589988.110336,
        symbol: "mi²",
        system: "imperial",
        labelKey: "units.area.squareMile"
      }
    },
    displayBands: {
      metric: [
        { maxBase: 10000, unit: "sqm" },
        { maxBase: 1000000, unit: "ha" },
        { unit: "sqkm" }
      ],
      imperial: [
        { maxBase: 0.09290304, unit: "sqin" },
        { maxBase: 4046.8564224, unit: "sqft" },
        { maxBase: 2589988.110336, unit: "ac" },
        { unit: "sqmi" }
      ]
    },
    inputUnits: {
      // Sq. miles excluded from inputs; still available for auto-picked outputs.
      metric: ["sqmm", "sqm", "ha", "sqkm"],
      imperial: ["sqin", "sqft", "ac"]
    },
    metricToImperialMap: {
      sqmm: "sqin",
      sqm: "sqft",
      ha: "ac",
      sqkm: "sqmi"
    }
  },

  volume: {
    base: "m3",
    defaultPrecision: 2,
    systemAgnostic: false,
    fixed: false,
    units: {
      m3: {
        toBase: 1,
        symbol: "m³",
        system: "metric",
        labelKey: "units.volume.cubicMeter"
      },
      l: {
        toBase: 0.001,
        symbol: "L",
        system: "metric",
        labelKey: "units.volume.liter"
      },
      ft3: {
        toBase: 0.028316846592,
        symbol: "ft³",
        system: "imperial",
        labelKey: "units.volume.cubicFoot"
      },
      yd3: {
        toBase: 0.764554857984,
        symbol: "yd³",
        system: "imperial",
        labelKey: "units.volume.cubicYard"
      }
    },
    displayBands: {
      metric: [{ maxBase: 1, unit: "l" }, { unit: "m3" }],
      imperial: [{ maxBase: 0.764554857984, unit: "ft3" }, { unit: "yd3" }]
    },
    inputUnits: {
      metric: ["m3", "l"],
      imperial: ["ft3", "yd3"]
    },
    metricToImperialMap: {
      m3: "yd3",
      l: "ft3"
    }
  },

  // ANGLE FAMILY
  // Canonical/base unit is radians to match model/internal math conventions.
  // UI defaults to degrees (see displayBands), but all conversions route through rad.
  // Invariant: any value passed as "base" for angle helpers is in radians.

  angle: {
    base: "rad",
    defaultPrecision: 1,
    systemAgnostic: true,
    fixed: false,
    units: {
      rad: {
        toBase: 1,
        symbol: "rad",
        system: "any",
        labelKey: "units.angle.radian"
      },
      deg: {
        toBase: Math.PI / 180, // degrees -> radians
        symbol: "°",
        system: "any",
        labelKey: "units.angle.degree"
      }
    },
    displayBands: {
      any: [{ unit: "deg" }]
    },
    inputUnits: {
      any: ["deg", "rad"]
    }
  },

  // PERCENTAGE FAMILY
  // Canonical/base unit is ratio (0..1 style), not percent points.
  // Example: 0.125 base == 12.5% display.
  // Invariant: any "base" value for percentage helpers is ratio.
  //`fixed: true` means there is only one display unit (pct) and no band-switching.
  // `systemAgnostic: true` covers the "use displayBands.any" lookup; both flags are set
  // so that code reading the config can distinguish "no system preference" from "no scaling".
  // The implementation treats them identically in the band-lookup path.

  percentage: {
    base: "ratio",
    defaultPrecision: 1,
    systemAgnostic: true,
    fixed: true,
    units: {
      ratio: {
        toBase: 1,
        symbol: "",
        system: "any",
        labelKey: "units.percentage.ratio"
      },
      pct: {
        toBase: 0.01,
        symbol: "%",
        system: "any",
        labelKey: "units.percentage.percent"
      }
    },
    displayBands: {
      any: [{ unit: "pct" }]
    },
    inputUnits: {
      any: ["pct"]
    }
  }
};

// ---------------------------------------------------------------------------
// Thin read helpers. Locale-aware formatting lives in utils/units/format.js.
// Everything below is pure and synchronous.
// ---------------------------------------------------------------------------

export function getQuantity(family) {
  const q = QUANTITIES[family];
  if (!q) {
    throw new Error(`Unknown quantity family: "${family}"`);
  }
  return q;
}

export function isKnownQuantityFamily(family) {
  return Boolean(family && QUANTITIES[family]);
}

/**
 * Given a family, the metric unit symbol returned by the API, and the active
 * system, returns the unit id to use for display/input.
 * Falls back to sourceSymbol if no mapping is found (e.g. system-agnostic families).
 */
export function getSystemUnit(family, sourceSymbol, system) {
  if (!isKnownQuantityFamily(family)) return sourceSymbol;
  const q = getQuantity(family);
  if (q.systemAgnostic) return sourceSymbol;
  if (system === "metric" || !system) return sourceSymbol;
  return q.metricToImperialMap?.[sourceSymbol] ?? sourceSymbol;
}

export function hasUnit(family, unitId) {
  if (!isKnownQuantityFamily(family) || !unitId) return false;
  return Boolean(getQuantity(family).units?.[unitId]);
}

export function getUnit(family, unit) {
  const q = getQuantity(family);
  const u = q.units[unit];
  if (!u) {
    throw new Error(`Unit "${unit}" is not defined for quantity "${family}"`);
  }
  return u;
}

export function getBaseUnit(family) {
  return getQuantity(family).base;
}

// Convert a value between two units in the same family (routes via base).
export function convert(value, fromUnit, toUnit, family) {
  if (fromUnit === toUnit) return value;
  const from = getUnit(family, fromUnit);
  const to = getUnit(family, toUnit);
  return (value * from.toBase) / to.toBase;
}

// Convert a value expressed in the family's base unit to `toUnit`.
export function fromBase(baseValue, toUnit, family) {
  return baseValue / getUnit(family, toUnit).toBase;
}

// Convert a value in `fromUnit` to the family's base unit.
export function toBase(value, fromUnit, family) {
  return value * getUnit(family, fromUnit).toBase;
}

// Given a value already in base unit + user system, return display unit.
// Uses absolute magnitude for picking (negative values preserve sign later).
export function pickDisplayUnit(baseValue, family, system) {
  const q = getQuantity(family);
  const bands =
    q.systemAgnostic || q.fixed ? q.displayBands.any : q.displayBands[system];

  if (!bands || bands.length === 0) {
    if (!q.systemAgnostic && !q.fixed) {
      throw new Error(
        `Unknown system "${system}" for quantity "${family}". Expected "metric" or "imperial".`
      );
    }
    return q.base;
  }

  const abs = Math.abs(baseValue);
  // Invariant: the last band always has no maxBase, so the loop always returns.
  for (let i = 0; i < bands.length; i += 1) {
    const band = bands[i];
    if (band.maxBase === undefined || abs < band.maxBase) {
      return band.unit;
    }
  }
}

// Units offered in input dropdowns for family + system.
// includeInternal=true ensures storage/base unit can be selected.
export function getInputUnits(
  family,
  system,
  { includeInternal = false } = {}
) {
  const q = getQuantity(family);
  const key = q.systemAgnostic || q.fixed ? "any" : system;
  const list = [...(q.inputUnits[key] || [])];
  if (includeInternal && !list.includes(q.base)) {
    list.unshift(q.base);
  }
  return list;
}

// Reverse lookup: unit code -> family.
// If unit id appears in multiple families, force explicit family.
const UNIT_TO_FAMILY = Object.entries(QUANTITIES).reduce((acc, [family, q]) => {
  Object.keys(q.units).forEach(unit => {
    if (unit in acc && acc[unit] !== family) acc[unit] = null;
    else acc[unit] = family;
  });
  return acc;
}, {});

export function getFamilyForUnit(unit) {
  const family = UNIT_TO_FAMILY[unit];
  if (family == null) {
    throw new Error(
      `Could not infer family for unit "${unit}"; pass family explicitly.`
    );
  }
  return family;
}
